import type { LE_CODES } from "@/constants/enums";
import type { ElectronicForm, ElectronicFormBE } from "@/views/electronicForm/electronicForm";
import { getGroupCodeAndCodeFromLookupMurn } from "@modernary/mui/components/lookup";
import { useRouter, type RouteParamsRaw } from "vue-router";

export type ElectronicFormRouteSuffix = "New" | "View" | "Edit" | "Base" | "Audit" | string;

/**
 * Returns a click handler with support for ctrl and middle clicks.
 *
 * @param {ElectronicFormRouteSuffix} view Route suffix to be used in the route name
 * @param {Function} [paramsBuilder] Optional method to build the route parameters. Defaults to using the items's id property.
 * @returns {Function} Method with ($event, paramsArg, newTab) args.
 */
export const createElectronicFormRowClickHandler = <T extends ElectronicForm | ElectronicFormBE>(
  view: ElectronicFormRouteSuffix = "View",
  paramsBuilder: (paramsArg: T) => RouteParamsRaw = (paramsArg) => ({ id: paramsArg.id }),
) => {
  const router = useRouter();

  return ($event: KeyboardEvent | PointerEvent | MouseEvent, paramsArg: T, newTab?: boolean) => {
    newTab =
      newTab ||
      $event.ctrlKey ||
      (($event instanceof MouseEvent || $event instanceof PointerEvent) && $event.button === 1);

    const newRoute = {
      name: getElectronicFormRouteNameFromItem(paramsArg, view),
      params: paramsBuilder(paramsArg),
    };

    if (newTab) {
      const url = router.resolve(newRoute).href;
      window.open(url, "_blank");
    } else {
      router.push(newRoute);
    }
  };
};

/**
 * Returns the route name for an electronic form.
 *
 * @param form The form to get the route name for.
 * @param view The view to get the route name for.
 * @returns The route name for the electronic form.
 */
export const getElectronicFormRouteName = (form: LE_CODES.ElectronicForm, view: ElectronicFormRouteSuffix = "View") => {
  return form + view;
};

/**
 * Returns the route name for an electronic form using an instance of an electronic form.
 *
 * @param item An instance of an electronic form.
 * @param view The view to get the route name for.
 * @returns The route name for the electronic form.
 */
export const getElectronicFormRouteNameFromItem = (
  item: ElectronicForm | ElectronicFormBE,
  view: ElectronicFormRouteSuffix = "View",
) => {
  return getElectronicFormRouteName(
    getGroupCodeAndCodeFromLookupMurn(item.formMurn).code as LE_CODES.ElectronicForm,
    view,
  );
};
